import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { TextField, Button, Grid, Typography, Box, FormHelperText } from "@mui/material";
import * as Yup from "yup";
import api from "src/store/features/api";
import { ChangePasswordApiArg } from "src/store/features/api/generated";

interface FormValues {
  oldPassword: string;
  confirmPassword: string;
  newPassword: string;
}

const ErrorText: React.FC = ({ children }: any) => <span style={{ color: "red" }}>{children}</span>;

const PasswordSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .max(255, "Password must be less than 50 characters")
    .required("Password is required"),
  newPassword: Yup.string()
    .notOneOf([Yup.ref("oldPassword"), null], "New password cannot be same as current password")
    .matches(/^(?=.*[a-z]).+$/, "Password must contain at least one lowercase letter")
    .matches(/^(?=.*[A-Z]).+$/, "Password must contain at least one uppercase letter")
    .matches(/^(?=.*\d).+$/, "Password must contain at least one digit")
    .matches(
      /^(?=.*[!@#$%^&*]).+$/,
      "Password must contain at least one special character (!@#$%^&)",
    )
    .min(6, "Password must be at least 6 characters")
    .max(255, "Password must be less than 50 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const ChangePassword: React.FC = () => {
  const [changePassword] = api.useChangePasswordMutation();
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const initialValues: FormValues = {
    oldPassword: "",
    confirmPassword: "",
    newPassword: "",
  };

  return (
    <Grid
      container
      alignItems="center"
      mt={3}
      style={{
        width: "100%",
        border: "1px solid #E5E9EB",
        borderRadius: "20px",
        padding: "25px 28px",
        marginBottom: "30px",
      }}>
      <Grid item xs={12}>
        <Typography style={{ fontWeight: 600, fontSize: "18px", lineHeight: "30px" }}>
          Change Password
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Formik
          initialValues={initialValues}
          validationSchema={PasswordSchema}
          onSubmit={async (values, { setStatus, setSubmitting, resetForm }): Promise<void> => {
            let body: ChangePasswordApiArg = {
              changePasswordDto: {
                currPassword: values.oldPassword,
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword,
              },
            };

            changePassword(body)
              .unwrap()
              .then(() => {
                setSubmitting(false);
                setSubmitted(true);
                resetForm();
                setStatus({ success: true });
              })
              .catch((error) => {
                setSubmitted(true);
                setSubmitError(error.message);
                setSubmitting(false);
                setStatus({ success: false });
              });
          }}>
          {({ isSubmitting }: any) => (
            <Form>
              <Grid pt={"28px"}>
                <Field
                  name="oldPassword"
                  label="Old Password"
                  type="password"
                  as={TextField}
                  fullWidth
                  InputProps={{
                    style: { borderRadius: "12px" }, // Adjust the border radius value as needed
                  }}
                  helperText={<ErrorMessage component={ErrorText} name="oldPassword" />}
                />
              </Grid>
              <Grid pt={2}>
                <Field
                  name="newPassword"
                  label="New Password"
                  type="password"
                  as={TextField}
                  fullWidth
                  InputProps={{
                    style: { borderRadius: "12px" }, // Adjust the border radius value as needed
                  }}
                  helperText={<ErrorMessage component={ErrorText} name="newPassword" />}
                />
              </Grid>
              <Grid pt={2}>
                <Field
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  as={TextField}
                  fullWidth
                  InputProps={{
                    style: { borderRadius: "12px" }, // Adjust the border radius value as needed
                  }}
                  helperText={<ErrorMessage component={ErrorText} name="confirmPassword" />}
                />
              </Grid>
              <Grid container>
                <Grid item xs={6} p={1} style={{ display: "flex", justifyContent: "start" }}>
                  <Box>
                    <FormHelperText
                      style={{ color: submitError ? "red" : "green", fontSize: "13px" }}>
                      {submitError ? submitError : submitted && "Success!"}
                    </FormHelperText>
                  </Box>
                </Grid>
                <Grid item xs={6} pt={"28px"} style={{ display: "flex", justifyContent: "end" }}>
                  <Button
                    disableRipple
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      border: "1px solid #002035",
                      borderRadius: "20px",
                      padding: "4px 12px",
                      height: "25px",
                      width: "70px",
                      textTransform: "none",
                    }}>
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ChangePassword;
